import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Settings2, Route, Shield, Plus, Trash2, Check, ChevronDown, ChevronUp, Sparkles, Info,
} from 'lucide-react';
import SectionHelp from '@/components/help/SectionHelp';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/** Textos claros para el dueño no técnico (S195). */
const MODEL_BLURBS = {
  'openai:gpt-5.6-luna': {
    tag: 'Recomendado · económico',
    blurb: 'Ideal para WhatsApp y atención diaria: rápido, barato y con herramientas (citas, catálogo, llamadas).',
    cost: 'Más barato de la familia GPT-5.6',
    when: 'Alto volumen de chats, bots de ventas, FAQs.',
  },
  'openai:gpt-5.6-terra': {
    tag: 'Equilibrado',
    blurb: 'Más capacidad que Luna sin el costo de Sol. Bueno si tus conversaciones son largas o técnicas.',
    cost: 'Costo medio',
    when: 'Asesoría detallada, verticales con mucho contexto.',
  },
  'openai:gpt-5.6-sol': {
    tag: 'Premium',
    blurb: 'Máxima calidad de razonamiento. Úsalo solo si el margen del lead lo justifica.',
    cost: 'Más caro',
    when: 'Casos complejos, cierre de alto ticket.',
  },
  'openai:gpt-4o-mini': {
    tag: 'Clásico económico',
    blurb: 'Modelo anterior estable. Si Luna da problemas de cuenta OpenAI, este es el plan B confiable.',
    cost: 'Económico',
    when: 'Respaldo o cuentas sin GPT-5.6 aún.',
  },
  'openai:gpt-4o': {
    tag: 'Clásico premium',
    blurb: 'GPT-4o completo. Más caro que Luna para el día a día.',
    cost: 'Alto',
    when: 'Calidad alta sin GPT-5.6.',
  },
  'xai:grok-3-mini': {
    tag: 'Grok',
    blurb: 'Modelo de xAI. Requiere tener Grok conectado en Integraciones.',
    cost: 'Medio',
    when: 'Si prefieres stack Grok en vez de OpenAI.',
  },
};

export default function AIRoutingConfig() {
  const [models, setModels] = useState([]);
  const [config, setConfig] = useState(null);
  const [routingRules, setRoutingRules] = useState([]);
  const [fallbackRules, setFallbackRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advTab, setAdvTab] = useState('routing');
  const [infoModel, setInfoModel] = useState(null);

  const load = async () => {
    try {
      const [mRes, cRes, rRes, fRes] = await Promise.all([
        api.get('/tenant/ai/models'),
        api.get('/tenant/ai/config'),
        api.get('/tenant/ai/routing-rules'),
        api.get('/tenant/ai/fallback-rules'),
      ]);
      setModels(mRes.data || []);
      setConfig(cRes.data || {});
      setRoutingRules(rRes.data || []);
      setFallbackRules(fRes.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selectModel = async (m) => {
    setSaving(true);
    try {
      const { data } = await api.put('/tenant/ai/config', {
        default_provider_key: m.provider_key,
        default_model_key: m.model_key,
      });
      setConfig(data);
      toast.success(`Listo: ahora usa ${m.display_name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PantallaCargando />;

  const current = config?.default_model_key;
  const openaiModels = models.filter(m => m.provider_key === 'openai' || m.provider_key === 'xai');

  return (
    <div className="space-y-6" data-testid="ai-routing-config">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[#F72585]" /> Modelo de IA
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Elige con un clic el cerebro que contesta a tus leads. No necesitas reglas técnicas.
        </p>
      </div>

      <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-emerald-900">
        Modelo actual:{' '}
        <strong>{models.find(m => m.model_key === current)?.display_name || current || 'Ninguno'}</strong>
        {config?.default_provider_key ? ` · ${config.default_provider_key}` : ''}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="model-cards">
        {openaiModels.map(m => {
          const b = MODEL_BLURBS[m.model_key] || {
            tag: m.quality_tier || 'IA',
            blurb: m.display_name,
            cost: m.quality_tier === 'premium' ? 'Costo alto' : m.quality_tier === 'economy' ? 'Económico' : 'Medio',
            when: 'Uso general',
          };
          const active = current === m.model_key;
          return (
            <button
              key={m.model_key}
              type="button"
              disabled={saving}
              onClick={() => selectModel(m)}
              className={`text-left rounded-2xl border p-4 transition-all hover:shadow-md ${
                active ? 'border-[#F72585] ring-2 ring-[#F72585]/20 bg-[#FFF5F9]' : 'border-[#E2E8F0] bg-white'
              }`}
              data-testid={`model-card-${m.model_key}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <p className="text-sm font-bold text-[#0F172A]">{m.display_name}</p>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[#F72585]">{b.tag}</span>
                </div>
                {active ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    <Check className="w-3 h-3" /> Activo
                  </span>
                ) : (
                  <button
                    type="button"
                    className="p-1 text-[#94A3B8] hover:text-[#0F172A]"
                    onClick={(e) => { e.stopPropagation(); setInfoModel({ ...m, ...b }); }}
                    aria-label="Más info"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-[#64748B] leading-relaxed mb-2">{b.blurb}</p>
              <p className="text-[11px] text-[#475569]"><strong>Costo:</strong> {b.cost}</p>
              <p className="text-[11px] text-[#475569]"><strong>Conviene para:</strong> {b.when}</p>
            </button>
          );
        })}
      </div>

      <Dialog open={!!infoModel} onOpenChange={() => setInfoModel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{infoModel?.display_name}</DialogTitle>
            <DialogDescription>{infoModel?.tag}</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2 text-[#475569]">
            <p>{infoModel?.blurb}</p>
            <p><strong>Costo relativo:</strong> {infoModel?.cost}</p>
            <p><strong>Cuándo usarlo:</strong> {infoModel?.when}</p>
            <p className="text-xs text-[#94A3B8]">ID técnico: {infoModel?.model_key}</p>
          </div>
          <Button onClick={() => { selectModel(infoModel); setInfoModel(null); }} className="bg-[#0F172A]">
            Usar este modelo
          </Button>
        </DialogContent>
      </Dialog>

      <button
        type="button"
        onClick={() => setShowAdvanced(v => !v)}
        className="flex items-center gap-2 text-xs font-medium text-[#64748B] hover:text-[#0F172A]"
      >
        {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        Avanzado (reglas por vertical y plan B) — opcional
      </button>

      {showAdvanced && (
        <div className="space-y-4 border border-[#E2E8F0] rounded-xl p-4">
          <SectionHelp module="ai_routing" title="las reglas de IA"
            description="Solo si tienes varios proveedores o quieres un modelo distinto por vertical."
            whatItDoesNot="No es el selector principal: arriba ya elegiste el modelo de todos los días." />
          <div className="flex gap-2 border-b border-[#E2E8F0]">
            {[{ key: 'routing', label: 'Por caso de uso', icon: Route }, { key: 'fallback', label: 'Plan B', icon: Shield }].map(t => (
              <button key={t.key} onClick={() => setAdvTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 ${advTab === t.key ? 'border-[#F72585] text-[#0F172A]' : 'border-transparent text-[#64748B]'}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
          {advTab === 'routing' && <RoutingTab rules={routingRules} setRules={setRoutingRules} models={models} />}
          {advTab === 'fallback' && <FallbackTab rules={fallbackRules} setRules={setFallbackRules} models={models} />}
        </div>
      )}
    </div>
  );
}

function RoutingTab({ rules, setRules, models }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ scope_type: 'vertical', scope_value: '', provider_key: '', model_key: '', priority: 0 });
  const handleAdd = async () => {
    try {
      const { data } = await api.post('/tenant/ai/routing-rules', form);
      setRules(prev => [...prev, data]);
      setAdding(false);
      toast.success('Regla creada');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const handleDelete = async (id) => {
    try {
      await api.delete(`/tenant/ai/routing-rules/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const modelLabel = (key) => models.find(m => m.model_key === key)?.display_name || key;
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold">Reglas ({rules.length})</h3>
        <Button size="sm" onClick={() => setAdding(!adding)} className="text-xs bg-[#0F172A]"><Plus className="w-3.5 h-3.5 mr-1" /> Nueva</Button>
      </div>
      {adding && (
        <div className="flex flex-wrap gap-2 items-center">
          <select className="border rounded px-2 py-1 text-xs" value={form.scope_type} onChange={e => setForm(f => ({ ...f, scope_type: e.target.value }))}>
            <option value="vertical">Vertical</option>
            <option value="channel">Canal</option>
            <option value="module">Módulo</option>
          </select>
          <input className="border rounded px-2 py-1 text-xs" placeholder="valor" value={form.scope_value} onChange={e => setForm(f => ({ ...f, scope_value: e.target.value }))} />
          <select className="border rounded px-2 py-1 text-xs" value={form.model_key} onChange={e => {
            const m = models.find(x => x.model_key === e.target.value);
            setForm(f => ({ ...f, model_key: e.target.value, provider_key: m?.provider_key || '' }));
          }}>
            <option value="">Modelo...</option>
            {models.map(m => <option key={m.model_key} value={m.model_key}>{m.display_name}</option>)}
          </select>
          <Button size="sm" onClick={handleAdd}>Guardar</Button>
        </div>
      )}
      {rules.length === 0 && <p className="text-xs text-[#94A3B8]">Sin reglas — se usa el modelo de las tarjetas de arriba.</p>}
      {rules.map(r => (
        <div key={r.id} className="flex justify-between text-xs border rounded-lg px-3 py-2">
          <span>{r.scope_type}:{r.scope_value || 'todos'} → {modelLabel(r.model_key)}</span>
          <button type="button" onClick={() => handleDelete(r.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
}

function FallbackTab({ rules, setRules, models }) {
  const [form, setForm] = useState({ primary_model_key: '', fallback_model_key: '', fallback_order: 1 });
  const handleAdd = async () => {
    try {
      const { data } = await api.post('/tenant/ai/fallback-rules', form);
      setRules(prev => [...prev, data]);
      setForm({ primary_model_key: '', fallback_model_key: '', fallback_order: 1 });
      toast.success('Plan B creado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const handleDelete = async (id) => {
    try {
      await api.delete(`/tenant/ai/fallback-rules/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-[#64748B]">Si el modelo principal falla, se usa el de respaldo automáticamente.</p>
      <div className="flex flex-wrap gap-2">
        <select className="border rounded px-2 py-1 text-xs" value={form.primary_model_key} onChange={e => setForm(f => ({ ...f, primary_model_key: e.target.value }))}>
          <option value="">Primario...</option>
          {models.map(m => <option key={m.model_key} value={m.model_key}>{m.display_name}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-xs" value={form.fallback_model_key} onChange={e => setForm(f => ({ ...f, fallback_model_key: e.target.value }))}>
          <option value="">Respaldo...</option>
          {models.map(m => <option key={m.model_key} value={m.model_key}>{m.display_name}</option>)}
        </select>
        <Button size="sm" onClick={handleAdd}>Agregar</Button>
      </div>
      {rules.map(r => (
        <div key={r.id} className="flex justify-between text-xs border rounded-lg px-3 py-2">
          <span>{r.primary_model_key} → {r.fallback_model_key}</span>
          <button type="button" onClick={() => handleDelete(r.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
}
